import React from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowUpRight, Mail, Phone, Linkedin, Globe,
  Mic, Target, Languages, LineChart, Blocks, ShieldCheck, PhoneCall, Zap,
  FileCheck2, BadgeDollarSign, Award, Handshake, HeadphonesIcon, CheckCircle2,
  FileText, Trophy, Building2, TrendingUp, Users, MapPin, Home, Warehouse, Compass, LifeBuoy, Search,
  Trophy as Trophy2
} from 'lucide-react';
import Reveal from '../components/Reveal';
import Seo from '../components/Seo';
import TestimonialCard from '../components/TestimonialCard';
import ContactForm from '../components/ContactForm';
import { businesses, testimonials, leadership, complianceGallery, clientLogos } from '../mockData';

const humanKey = (k) => k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());

const FEATURE_ICONS = {
  // AI CRM
  'AI Voice Agents': Mic,
  'Lead Intelligence': Target,
  'Multi-language Reach': Languages,
  'Revenue Analytics': LineChart,
  '50+ Integrations': Blocks,
  'Enterprise Security': ShieldCheck,
  'Auto Dialer & Handoff': PhoneCall,
  'Deal Automation': Zap,
  // Compliances
  'Single-Window Filing': FileCheck2,
  'Transparent Pricing': BadgeDollarSign,
  '15+ Years of Expertise': Award,
  'Global Client Base': Globe,
  'Fast Turnaround': Zap,
  'Human Support': Handshake,
  'Audit-Ready Documentation': FileText,
  'End-to-End Ownership': CheckCircle2,
  // Styx
  'Franchise Program': Building2,
  'Attractive Unit ROI': TrendingUp,
  'Premium Facilities': Trophy,
  '25+ Locations': MapPin,
  'Diverse Programming': Users,
  'Signature F&B': Award,
  // Locacres
  'Curated Inventory': FileCheck2,
  'Multiple Asset Classes': Building2,
  'Investment Advisory': TrendingUp,
  'Senior Consultants': Users,
  'Nine Global Markets': Globe,
  'End-to-End Handling': CheckCircle2,
  'Concierge Support': LifeBuoy,
  'Structured Search': Search
};

const PRODUCT_ICONS = {
  // CRM products
  'Sales Automation': Zap,
  'AI Voice Agents': Mic,
  'Lead Intelligence': Target,
  'Support Automation': HeadphonesIcon,
  'CRM Integrations': Blocks,
  'Analytics Suite': LineChart,
  // Styx activities
  'Pickleball': Trophy2,
  'Snooker': Target,
  'Box Cricket': TrendingUp,
  'Paddle Tennis': Trophy2,
  'Sports Bar': Users,
  'Game Zone': Award,
  // Locacres properties
  'Luxury Villas': Home,
  'Premium Apartments': Building2,
  'Commercial Assets': Building2,
  'Warehousing': Warehouse,
  'Homestay Rentals': Compass,
  'Contemporary Homes': Home
};

const BusinessDetailPage = () => {
  const { businessId } = useParams();
  const business = businesses.find((b) => b.id === businessId);

  if (!business) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-alabaster">
        <div className="text-center">
          <div className="eyebrow mb-4">404</div>
          <h1 className="font-display text-5xl text-brand-oxford mb-6">Business not found</h1>
          <Link to="/" className="text-brand-oxford border-b border-brand-oxford pb-1">Return home</Link>
        </div>
      </div>
    );
  }

  const accent = `rgb(var(--sub-${business.subBrand}))`;
  const businessTestimonials = testimonials.filter((t) => t.business === business.id);
  const items = business.products || business.services || business.activities || business.properties || [];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': business.id === 'locacres' ? 'RealEstateAgent' : business.id === 'styx-cafe' ? 'Restaurant' : 'Service',
    name: business.name,
    description: business.longDescription,
    url: business.website,
    email: business.email,
    telephone: business.phone,
    parentOrganization: { '@type': 'Organization', name: 'Lockene' }
  };

  return (
    <>
      <Seo
        title={`${business.name} · Lockene`}
        description={business.longDescription}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section
        className="bg-brand-alabaster pt-28 md:pt-36 pb-16"
        aria-labelledby="biz-heading"
        data-testid={`hero-${business.id}`}
      >
        <div className="container-editorial">
          <div className="border-t border-brand-hairline mb-14" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-6">
                <span className="inline-block w-10 h-px" style={{ backgroundColor: accent }} aria-hidden />
                <span className="text-[11px] tracking-[0.18em] uppercase font-bold" style={{ color: accent }}>
                  {business.tagline}
                </span>
              </div>
              <h1 id="biz-heading" className="font-display text-5xl md:text-7xl leading-[1.03] tracking-tight text-brand-oxford">
                {business.name}
              </h1>
              <p className="mt-8 text-lg md:text-xl text-brand-slate leading-relaxed max-w-2xl">
                {business.longDescription}
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  href="#contact"
                  data-testid="biz-cta-contact"
                  className="inline-flex items-center gap-3 bg-brand-oxford text-brand-alabaster px-7 py-4 text-[12px] font-semibold tracking-[0.16em] uppercase transition-colors hover:bg-brand-gold hover:text-brand-oxford focus-ring"
                >
                  Speak with an Advisor <span aria-hidden>→</span>
                </a>
                <a
                  href={business.website}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="biz-cta-website"
                  className="inline-flex items-center gap-3 border border-brand-oxford text-brand-oxford px-7 py-4 text-[12px] font-semibold tracking-[0.16em] uppercase transition-colors hover:bg-brand-oxford hover:text-brand-alabaster focus-ring"
                >
                  Visit Website <ArrowUpRight size={14} strokeWidth={1.5} />
                </a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -top-3 -left-3 w-24 h-24 border pointer-events-none" style={{ borderColor: accent, opacity: 0.4 }} aria-hidden />
                <div className="relative bg-white border border-brand-hairline overflow-hidden">
                  <img
                    src={business.heroImage}
                    alt={business.name}
                    className="w-full h-[380px] md:h-[440px] object-cover"
                    loading="eager"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 w-24 h-24 border border-brand-gold pointer-events-none" aria-hidden />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      {business.stats && (
        <section className="bg-white border-y border-brand-hairline py-14" aria-label="Key metrics" data-testid={`stats-${business.id}`}>
          <div className="container-editorial grid grid-cols-2 md:grid-cols-4 gap-y-8">
            {Object.entries(business.stats).map(([k, v], i) => (
              <div key={k} className={i !== 0 ? 'md:border-l md:border-brand-hairline md:pl-8' : 'md:pl-0'}>
                <div className="font-display text-4xl md:text-5xl text-brand-oxford leading-none">{v}</div>
                <div className="mt-3 eyebrow">{humanKey(k)}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Features */}
      <section className="section bg-brand-alabaster" aria-labelledby="feat-heading" data-testid={`features-${business.id}`}>
        <div className="container-editorial">
          <Reveal className="max-w-2xl mb-14">
            <div className="flex items-center gap-3 mb-6">
              <span className="hairline" aria-hidden />
              <span className="eyebrow">What sets us apart</span>
            </div>
            <h2 id="feat-heading" className="font-display text-4xl md:text-5xl leading-tight text-brand-oxford">
              A focused practice with an operator&apos;s mindset.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {business.features.map((f, i) => {
              const Icon = FEATURE_ICONS[f.title] || CheckCircle2;
              return (
                <Reveal key={f.title} delay={i * 60}>
                  <div className="bg-white border border-brand-hairline p-8 h-full hover:shadow-lift transition-shadow group">
                    <div
                      className="w-12 h-12 flex items-center justify-center border transition-colors"
                      style={{ borderColor: accent, color: accent }}
                      aria-hidden
                    >
                      <Icon size={22} strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-6 text-lg font-semibold text-brand-oxford">{f.title}</h3>
                    <p className="mt-3 text-brand-slate leading-relaxed">{f.description}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Products / Services / Activities / Properties */}
      {items.length > 0 && (
        <section className="section bg-white border-y border-brand-hairline" aria-labelledby="items-heading">
          <div className="container-editorial">
            <Reveal className="max-w-2xl mb-14">
              <div className="flex items-center gap-3 mb-6">
                <span className="hairline" aria-hidden />
                <span className="eyebrow">
                  {business.products ? 'Products' : business.services ? 'Services' : business.activities ? 'Programming' : 'Property Classes'}
                </span>
              </div>
              <h2 id="items-heading" className="font-display text-4xl md:text-5xl leading-tight text-brand-oxford">
                {business.products ? 'What we build.' : business.services ? 'What we file.' : business.activities ? 'What we host.' : 'What we broker.'}
              </h2>
            </Reveal>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {items.map((item, i) => {
                const withIcon = typeof item === 'object' && item.icon;
                const name = withIcon ? item.name : item;
                const Icon = PRODUCT_ICONS[name] || CheckCircle2;
                return (
                  <Reveal key={name} delay={i * 40}>
                    {withIcon ? (
                      <figure className="group bg-white border border-brand-hairline h-full flex flex-col hover:shadow-lift transition-shadow duration-300 overflow-hidden">
                        <div className="relative aspect-square overflow-hidden bg-brand-alabaster">
                          <img
                            src={item.icon}
                            alt={name}
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                          />
                          <div
                            className="absolute inset-0 pointer-events-none"
                            style={{ boxShadow: `inset 0 -1px 0 rgb(var(--sub-${business.subBrand}))` }}
                            aria-hidden
                          />
                        </div>
                        <figcaption className="px-4 py-4 md:px-5 md:py-5 border-t border-brand-hairline text-center">
                          <p className="font-semibold text-brand-oxford text-sm md:text-[15px] leading-snug">{name}</p>
                        </figcaption>
                      </figure>
                    ) : (
                      <div className="bg-white border border-brand-hairline p-6 h-full flex flex-col items-center text-center gap-4 hover:border-brand-oxford hover:shadow-ambient transition-all">
                        <div
                          className="w-14 h-14 flex items-center justify-center border"
                          style={{ borderColor: accent, color: accent }}
                          aria-hidden
                        >
                          <Icon size={26} strokeWidth={1.5} />
                        </div>
                        <p className="font-semibold text-brand-oxford text-sm leading-snug">{name}</p>
                      </div>
                    )}
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Locations (Locacres only) */}
      {business.locations && (
        <section className="section bg-brand-alabaster" aria-labelledby="locs-heading">
          <div className="container-editorial">
            <Reveal className="max-w-2xl mb-14">
              <div className="flex items-center gap-3 mb-6">
                <span className="hairline" aria-hidden />
                <span className="eyebrow">Global Markets</span>
              </div>
              <h2 id="locs-heading" className="font-display text-4xl md:text-5xl leading-tight text-brand-oxford">
                Where we transact.
              </h2>
            </Reveal>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {business.locations.map((loc, i) => (
                <Reveal key={loc} delay={i * 40}>
                  <div className="border border-brand-hairline bg-white p-5">
                    <div className="text-[11px] tracking-[0.16em] uppercase text-brand-slate/70">City</div>
                    <div className="font-display text-2xl text-brand-oxford mt-1">{loc}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Clients strip (Compliances only) */}
      {business.clients && (
        <section className="section bg-white border-y border-brand-hairline" aria-labelledby="clients-heading" data-testid="compliance-clients">
          <div className="container-editorial">
            <Reveal className="max-w-2xl mb-14">
              <div className="flex items-center gap-3 mb-6">
                <span className="hairline" aria-hidden />
                <span className="eyebrow">Selected Clients</span>
              </div>
              <h2 id="clients-heading" className="font-display text-4xl md:text-5xl leading-tight text-brand-oxford">
                Trusted by manufacturers across four continents.
              </h2>
              <p className="mt-6 text-lg text-brand-slate leading-relaxed">
                A small selection from our client roster of 18,000+ importers, manufacturers and global brands.
              </p>
            </Reveal>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 border-t border-l border-brand-hairline">
              {clientLogos.map((c) => (
                <div
                  key={c.id}
                  data-testid={`compliance-client-${c.id}`}
                  className="h-24 md:h-28 flex items-center justify-center px-6 py-4 border-r border-b border-brand-hairline"
                >
                  <img
                    src={c.src}
                    alt={c.alt}
                    loading="lazy"
                    decoding="async"
                    className="max-w-full max-h-full object-contain grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all duration-500"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery — Compliances only: on-site audits */}
      {business.id === 'lockene-compliances' && (
        <section className="section bg-brand-alabaster" aria-labelledby="gallery-heading" data-testid="compliance-gallery">
          <div className="container-editorial">
            <Reveal className="max-w-2xl mb-14">
              <div className="flex items-center gap-3 mb-6">
                <span className="hairline" aria-hidden />
                <span className="eyebrow">Compliance in Action</span>
              </div>
              <h2 id="gallery-heading" className="font-display text-4xl md:text-5xl leading-tight text-brand-oxford">
                On-site audits &amp; factory visits.
              </h2>
              <p className="mt-6 text-lg text-brand-slate leading-relaxed">
                Our senior officers travel to client factories in India and abroad — reviewing documentation, product samples
                and production processes against applicable Indian standards before submission.
              </p>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              {complianceGallery.map((g, i) => (
                <Reveal key={g.id} delay={i * 70}>
                  <figure className="bg-white border border-brand-hairline overflow-hidden h-full flex flex-col group">
                    <div className="relative overflow-hidden">
                      <img
                        src={g.image}
                        alt={g.title}
                        loading="lazy"
                        className="w-full h-64 object-cover group-hover:scale-[1.03] transition-transform duration-700"
                      />
                    </div>
                    <figcaption className="p-7 flex-1 flex flex-col">
                      <div
                        className="text-[11px] tracking-[0.16em] uppercase font-bold"
                        style={{ color: accent }}
                      >
                        {g.location}
                      </div>
                      <h3 className="font-display text-2xl text-brand-oxford mt-2">{g.title}</h3>
                      <p className="mt-3 text-brand-slate leading-relaxed flex-1">{g.description}</p>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Leadership — Compliances & AI CRM show group founders */}
      {(business.id === 'lockene-compliances' || business.id === 'lockene-crm') && (
        <section className="section bg-white border-y border-brand-hairline" aria-labelledby="lead-heading" data-testid={`leadership-${business.id}`}>
          <div className="container-editorial">
            <Reveal className="max-w-2xl mb-14">
              <div className="flex items-center gap-3 mb-6">
                <span className="hairline" aria-hidden />
                <span className="eyebrow">Leadership</span>
              </div>
              <h2 id="lead-heading" className="font-display text-4xl md:text-5xl leading-tight text-brand-oxford">
                Founders on the file.
              </h2>
            </Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              {leadership.map((p, i) => (
                <Reveal key={p.name} delay={i * 80}>
                  <article className="bg-brand-alabaster border border-brand-hairline grid grid-cols-1 md:grid-cols-5 h-full">
                    <div className="md:col-span-2 relative overflow-hidden bg-white">
                      <img src={p.image} alt={p.name} className="w-full h-64 md:h-full object-cover object-top" loading="lazy" />
                    </div>
                    <div className="md:col-span-3 p-7 md:p-8 flex flex-col">
                      <div className="text-[11px] tracking-[0.16em] uppercase font-bold" style={{ color: accent }}>{p.role}</div>
                      <h3 className="font-display text-2xl md:text-3xl text-brand-oxford mt-2">{p.name}</h3>
                      <p className="mt-4 text-brand-slate leading-relaxed">{p.bio}</p>
                      <div className="mt-5 flex flex-wrap gap-2">
                        {p.expertise.map((tag) => (
                          <span key={tag} className="text-[11px] tracking-[0.14em] uppercase font-semibold text-brand-slate border border-brand-hairline px-2.5 py-1">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <a
                        href={p.linkedin}
                        target="_blank"
                        rel="noreferrer noopener"
                        data-testid={`lead-linkedin-${p.name.replace(/\W+/g, '-').toLowerCase()}`}
                        className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand-oxford border-b border-brand-oxford pb-1 self-start hover:gap-3 transition-all"
                      >
                        <Linkedin size={14} strokeWidth={1.5} /> Connect on LinkedIn
                      </a>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Mission */}
      <section className="section bg-brand-oxford text-brand-alabaster" aria-labelledby="mission-heading">
        <div className="container-editorial max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="inline-block w-10 h-px bg-brand-gold" aria-hidden />
            <span className="eyebrow text-brand-gold/90">Our Mission</span>
          </div>
          <blockquote className="font-display text-3xl md:text-5xl leading-[1.15] tracking-tight text-brand-alabaster">
            “{business.mission}”
          </blockquote>
        </div>
      </section>

      {/* Testimonials */}
      {businessTestimonials.length > 0 && (
        <section className="section bg-brand-alabaster" aria-labelledby="biz-testi-heading">
          <div className="container-editorial">
            <Reveal className="max-w-2xl mb-14">
              <div className="flex items-center gap-3 mb-6">
                <span className="hairline" aria-hidden />
                <span className="eyebrow">Client Voices</span>
              </div>
              <h2 id="biz-testi-heading" className="font-display text-4xl md:text-5xl leading-tight text-brand-oxford">
                In their own words.
              </h2>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
              {businessTestimonials.slice(0, 3).map((t, i) => (
                <Reveal key={t.id} delay={i * 70} className="h-full">
                  <TestimonialCard testimonial={t} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact */}
      <section id="contact" className="section bg-white border-t border-brand-hairline" aria-labelledby="biz-contact-heading" data-testid={`contact-section-${business.id}`}>
        <div className="container-editorial grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-6">
              <span className="hairline" aria-hidden />
              <span className="eyebrow">Speak with {business.name.split(' ')[0]}</span>
            </div>
            <h2 id="biz-contact-heading" className="font-display text-4xl md:text-5xl leading-tight text-brand-oxford">
              A senior advisor will respond within one business day.
            </h2>
            <div className="mt-8 space-y-4 text-brand-slate">
              <a href={`mailto:${business.email}`} className="flex items-center gap-3 hover:text-brand-oxford transition-colors">
                <Mail size={16} strokeWidth={1.5} />
                <span>{business.email}</span>
              </a>
              <a href={`tel:${business.phone.replace(/\s+/g, '')}`} className="flex items-center gap-3 hover:text-brand-oxford transition-colors">
                <Phone size={16} strokeWidth={1.5} />
                <span>{business.phone}</span>
              </a>
              <a href={business.website} target="_blank" rel="noreferrer noopener" className="flex items-center gap-3 hover:text-brand-oxford transition-colors">
                <Globe size={16} strokeWidth={1.5} />
                <span>{business.website.replace(/^https?:\/\//, '')}</span>
              </a>
              {business.linkedin && (
                <a href={business.linkedin} target="_blank" rel="noreferrer noopener" className="flex items-center gap-3 hover:text-brand-oxford transition-colors">
                  <Linkedin size={16} strokeWidth={1.5} />
                  <span>LinkedIn</span>
                </a>
              )}
            </div>
          </div>
          <div className="lg:col-span-8">
            <ContactForm context={business.id} business={business} />
          </div>
        </div>
      </section>
    </>
  );
};

export default BusinessDetailPage;
